/* ═══════════════════════════════════════════════════════════════════════
   FizzyIQ — Frosted-glass cart drawer
   Port of /design_handoff_cart_drawer/. Dawn-native — uses the live
   Shopify cart (not localStorage), refreshes via the Section Rendering
   API (`?sections=fizzy-cart-drawer`), and exposes a small public API.

   Open triggers (delegated):
     • [data-open-cart]            — explicit attribute (preferred)
     • a.cart-pill (no href= /cart) — header cart button
   Close triggers:
     • [data-close]                — scrim + close button
     • Escape key

   Mutations call Shopify cart endpoints, then re-fetch the section HTML
   and replace the body/count/subtotal/shipping fields in place.

   Public API (window.FizzyCart):
     open()         — open the drawer
     close()        — close it
     refresh()      — re-fetch /cart.js + the section, replace fields
     add(formData)  — POST to /cart/add.js, then refresh + open
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  if (window.__fizzyCartDrawerInit) return;
  window.__fizzyCartDrawerInit = true;

  var SECTION_ID = 'fizzy-cart-drawer';
  var SHIP_GOAL_CENTS = 6000;
  var FLAVOR_MAP = {
    'Berry Cherry':        { sw: '#2C4A8B', img: 'fizzy-pouch-bc.png' },
    'Strawberry Lemonade': { sw: '#C8323C', img: 'fizzy-pouch-sl.png' },
    'Watermelon':          { sw: '#0A5D56', img: 'fizzy-pouch-wm.png' },
    'Orange Creamsicle':   { sw: '#C23A12', img: 'fizzy-pouch-ob.png' }
  };

  /* ── helpers ──────────────────────────────────────────────────────── */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function syncBubbles(count) {
    $$('.cart-pill .bubble, [data-cart-count]').forEach(function (el) {
      el.textContent = count;
      if (count > 0) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  }

  /* ── open / close ─────────────────────────────────────────────────── */
  function root() { return document.getElementById('fizzyCartDrawer'); }

  function open() {
    var el = root();
    if (!el) return;
    el.classList.add('open');
    el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    /* Mutually exclusive with the mobile menu (and any other overlay) —
       announce that the cart is opening so others close themselves. */
    document.dispatchEvent(new CustomEvent('fizzy:overlay-open', { detail: 'cart' }));
    // Move focus to the close button for keyboard users
    var closeBtn = el.querySelector('.fcd-x');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  /* Close the cart if a different overlay (e.g. the mobile menu) opens. */
  document.addEventListener('fizzy:overlay-open', function (e) {
    if (e && e.detail !== 'cart') close();
  });

  function close() {
    var el = root();
    if (!el) return;
    el.classList.remove('open');
    el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  /* ── section refresh ──────────────────────────────────────────────── */
  // Re-fetch the rendered section HTML + parse, then swap the live regions
  // inside the existing drawer in place. We do NOT replace the whole root
  // because that would also rip out the `.open` class and break focus.
  function refresh(highlightLineKey) {
    return fetch('/?sections=' + SECTION_ID, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data[SECTION_ID];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.getElementById('fizzyCartDrawer');
        var live = root();
        if (!fresh || !live) return;

        // Replace the live regions
        var swaps = [
          ['[data-fcd-list]', '[data-fcd-list]'],
          ['[data-fcd-count]', '[data-fcd-count]'],
          ['[data-fcd-sub]', '[data-fcd-sub]'],
          ['.fcd-empty', '.fcd-empty'],
          ['[data-fcd-upsell]', '[data-fcd-upsell]']
        ];
        swaps.forEach(function (pair) {
          var src = fresh.querySelector(pair[0]);
          var dst = live.querySelector(pair[1]);
          if (src && dst) dst.innerHTML = src.innerHTML;
        });

        // Toggle the empty class on root to match the new state
        if (fresh.classList.contains('empty')) live.classList.add('empty');
        else live.classList.remove('empty');

        // Re-mark a line for the just-added pop animation if we know the key
        if (highlightLineKey) {
          var added = live.querySelector('[data-line-key="' + highlightLineKey + '"]');
          if (added) {
            added.classList.add('just-added');
            setTimeout(function () { added.classList.remove('just-added'); }, 700);
          }
        }

        // Pull the count off the freshly rendered DOM for the bubble sync
        var countMatch = (fresh.querySelector('[data-fcd-count]') || {}).textContent || '';
        var n = parseInt(countMatch.replace(/[^\d]/g, ''), 10) || 0;
        syncBubbles(n);
      })
      .catch(function (err) {
        console.error('[FizzyCart] refresh failed', err);
      });
  }

  /* ── line-item mutations ──────────────────────────────────────────── */
  function changeLine(key, newQty, lineEl) {
    if (lineEl) lineEl.classList.add('is-busy');
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: newQty })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refresh(); })
      .catch(function (err) {
        console.error('[FizzyCart] change failed', err);
        if (lineEl) lineEl.classList.remove('is-busy');
      });
  }

  /* ── add to cart ──────────────────────────────────────────────────── */
  // Accepts a FormData (from the PDP form) or a plain object. Posts to
  // /cart/add.js, then refreshes + opens. Returns the added line key so
  // the caller can do anything with it (we use it for the pop animation).
  function add(formDataOrObj) {
    var body, headers;
    if (formDataOrObj instanceof FormData) {
      body = formDataOrObj;
      headers = { 'Accept': 'application/javascript' };
    } else {
      body = JSON.stringify(formDataOrObj);
      headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: headers,
      body: body
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      })
      .then(function (added) {
        // /cart/add.js returns the added item; .key is the line key
        var key = added && added.key;
        return refresh(key).then(function () {
          open();
          return added;
        });
      });
  }

  /* ── event wiring ─────────────────────────────────────────────────── */
  // Open triggers run in the CAPTURE phase on WINDOW: app embeds (Rebuy
  // Smart Cart) bind capturing handlers on document before this deferred
  // script runs and swallow real clicks on cart anchors. Window capture
  // fires before document capture regardless of registration order, so
  // our drawer always wins the click.
  window.addEventListener('click', function (e) {
    var openT = e.target.closest && e.target.closest('[data-open-cart], a.cart-pill');
    if (openT) {
      e.preventDefault();
      e.stopPropagation();
      open();
    }
  }, true);

  document.addEventListener('click', function (e) {
    // Inside the drawer
    var el = root();
    if (!el || !el.contains(e.target)) return;

    if (e.target.closest('[data-close]')) {
      e.preventDefault();
      close();
      return;
    }

    /* ── Bundle dropdown — expand/collapse the flavor list on a bundle line. */
    var bundleToggle = e.target.closest('[data-fcd-bundle-toggle]');
    if (bundleToggle) {
      e.preventDefault();
      var blist = bundleToggle.parentElement.querySelector('.fcd-bundle-list');
      var bopen = bundleToggle.getAttribute('aria-expanded') === 'true';
      bundleToggle.setAttribute('aria-expanded', bopen ? 'false' : 'true');
      if (blist) blist.hidden = bopen;
      return;
    }

    /* ── Upsell quick-add ─────────────────────────────────────────
       "Add one for me" — adds the real Ages 14+ / Try It Once variant of the
       chosen flavor (falls back to default variant + properties if not found). */
    var upsellBtn = e.target.closest('[data-fcd-upsell-add]');
    if (upsellBtn) {
      e.preventDefault();
      if (upsellBtn.disabled) return;
      var upsellWrap = upsellBtn.closest('[data-fcd-upsell]');
      var variantId = upsellWrap && upsellWrap.getAttribute('data-variant-id');
      if (!variantId) {
        console.error('[FizzyIQ Upsell] missing data-variant-id on .fcd-upsell');
        return;
      }
      var flavorName = upsellBtn.getAttribute('data-flavor-name') || 'Berry Cherry';
      upsellBtn.disabled = true;
      var adultV = (function () {
        var list = window.__fizzyVariants || [];
        for (var i = 0; i < list.length; i++) {
          var o = list[i].options || [];
          if (o.indexOf(flavorName) > -1 && o.indexOf('Ages 14+') > -1 && o.indexOf('Try It Once') > -1) return list[i];
        }
        return null;
      })();
      var addItem = adultV ? { id: adultV.id, quantity: 1 } : { id: variantId, quantity: 1, properties: { Flavor: flavorName, Plan: 'One-time' } };
      add({ items: [addItem] })
        .then(function (response) {
          var addedLines = (response && response.items) || [];
          var key = addedLines.length ? addedLines[addedLines.length - 1].key : null;
          return refresh(key);
        })
        .catch(function (err) {
          console.error('[FizzyIQ Upsell] add failed', err);
        })
        .then(function () {
          /* Re-query the button because refresh() replaced the upsell
             markup — the original element reference is now detached. */
          var liveWrap = root() && root().querySelector('[data-fcd-upsell]');
          var liveBtn = liveWrap && liveWrap.querySelector('[data-flavor="' + upsellBtn.getAttribute('data-flavor') + '"]');
          if (liveBtn) liveBtn.disabled = false;
        });
      return;
    }

    // Empty-state "Shop Fizzy Focus" CTA — if we're already on the
    // linked page, just close the drawer instead of triggering a
    // no-op navigation. Otherwise let the anchor navigate normally.
    var shopCta = e.target.closest('[data-fcd-shop-cta]');
    if (shopCta) {
      try {
        var targetPath = new URL(shopCta.href, window.location.origin).pathname;
        if (window.location.pathname === targetPath) {
          e.preventDefault();
          close();
        }
      } catch (err) {
        // bad URL or no window.location — let the link do its default thing
      }
      return;
    }

    var line = e.target.closest('[data-fcd-item]');
    if (!line) return;
    var key = line.getAttribute('data-line-key');
    var qtyEl = line.querySelector('[data-fcd-qty]');
    var qty = parseInt((qtyEl && qtyEl.textContent) || '1', 10);

    if (e.target.closest('[data-fcd-inc]')) {
      e.preventDefault();
      changeLine(key, qty + 1, line);
      return;
    }
    if (e.target.closest('[data-fcd-dec]')) {
      e.preventDefault();
      if (qty > 1) changeLine(key, qty - 1, line);
      return;
    }
    if (e.target.closest('[data-fcd-rm]')) {
      e.preventDefault();
      changeLine(key, 0, line);
      return;
    }
  });

  // Esc closes
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var el = root();
    if (el && el.classList.contains('open')) close();
  });

  /* ── public API ───────────────────────────────────────────────────── */
  window.FizzyCart = {
    open: open,
    close: close,
    refresh: refresh,
    add: add,
    FLAVOR_MAP: FLAVOR_MAP
  };

  // Initial setup: bubble sync + redundant direct scrim binding.
  // The delegated [data-close] handler above already closes on scrim
  // clicks — but a direct listener guarantees it works even if some
  // higher-priority page click handler ever swallows the bubbled event.
  function bootstrap() {
    var countEl = $('[data-fcd-count]');
    if (countEl) {
      var n = parseInt(countEl.textContent.replace(/[^\d]/g, ''), 10) || 0;
      syncBubbles(n);
    }
    var scrim = document.querySelector('#fizzyCartDrawer .fcd-scrim');
    if (scrim) {
      scrim.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        close();
      });
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
